import { Injectable } from "@angular/core";
import { Subject } from "rxjs";

import * as moment from "moment";
import {
  CommunityNotificationData,
  NotificationData,
  NotificationType,
} from "./dialog-notifications.model";
import { SettingsLocalService } from "../../pages/settings/settings.service";

/**
 * Service responsible for managing dialog notifications within the application.
 * Handles scheduling, displaying, and managing notification timers for:
 * - Score Reminder notifications (Margin of Safety alerts)
 * - Science Bite notifications (Brain Science alerts)
 * 
 * Notifications are scheduled based on user settings and trigger at specified times,
 * playing sounds and displaying dialog notifications to the user.
 */
@Injectable({
  providedIn: "root",
})
export class DialogNotificationService {
  // Observable subject for notifying subscribers when notifications change
  private notificationSource = new Subject<NotificationData[]>();
  notificationChanged = this.notificationSource.asObservable();
  
  // Array of active notification data
  private notifications: NotificationData[] = [];
  
  // Timer references for scheduled notifications
  scoreAgainTimer: any; // Timer for Score Reminder notifications
  scienceBiteTimer: any; // Timer for Science Bite notifications
  scoreAgainMorningTimer: any; // Timer for morning Score Reminder notifications (deprecated)
  scienceBiteMorningTimer: any; // Timer for morning Science Bite notifications (deprecated)
  
  // Visibility flags for notification dialogs
  showScoreAgain: boolean = false; // Whether Score Reminder notification is currently visible
  showScienceBite: boolean = false; // Whether Science Bite notification is currently visible
  // Plan Reminder has no backend-driven alert time yet, so it defaults visible until dismissed
  // rather than being scheduled like the two types above (see refreshNotifications()).
  showPlanReminder: boolean = true;

  // Active Community (NodeBB) notification cards (FLIP-161). Unlike the show*
  // booleans above, this is a list (more than one can be active at once) and is
  // deliberately never persisted to localStorage - Community notifications have no
  // read/removed state, they just live in memory for the current session.
  communityNotifications: CommunityNotificationData[] = [];

  // Timeout references for morning notification scheduling (currently disabled)
  scienceBitesTimeout: any;
  marginOfSafetyFirstTimeout: any;
  marginOfSafetySecondTimeout: any;

  constructor(private settingsService: SettingsLocalService) {}

  /**
   * Pulls current user settings and (re)schedules Score Reminder and Science Bite
   * notifications from them. This is the single source of truth for "what should
   * be scheduled right now" - safe to call repeatedly (e.g. on login and again
   * whenever settings change), since it clears existing notifications first.
   *
   * Does not touch Plan Reminder, which has no backend-driven alert time and is
   * managed separately via setPlanReminderVisibility()/refreshNotifications().
   */
  refreshSchedule(): void {
    this.settingsService.getSettings$().subscribe((settings) => {
      // Clear existing Score Reminder / Science Bite notifications before adding new ones.
      // Scoped to these two types only - deliberately leaves Plan Reminder's visibility
      // untouched (unlike removeAllNotifications(), which would also reset it).
      this.setScoreAgainVisibility(false);
      this.setScienceBiteVisibility(false);
      this.notifications = this.notifications.filter(
        (n) =>
          n.notificationType !== NotificationType.ScoreReminder &&
          n.notificationType !== NotificationType.ScienceBite
      );

      // Add Science Bite notification if configured
      if (settings.brainScienceBiteNextAlertDateTime) {
        this.addNotification(
          new NotificationData(
            NotificationType.ScienceBite,
            settings.brainScienceBiteNextAlertDateTime.toString()
          )
        );
      }

      // For Score Reminder, use API-provided alert times
      if (settings.isMarginOfSafetyAlertOn) {
        const firstAlertTime = settings.marginOfSafetyAlertDateTime;
        const secondAlertTime = settings.marginOfSafetyNextAlertDateTime;
        const now = moment();

        // Determine which alert time to use (whichever comes next)
        let nextAlertTime: string;
        if (firstAlertTime && secondAlertTime) {
          const firstMoment = moment(firstAlertTime);
          const secondMoment = moment(secondAlertTime);
          if (now.isBefore(firstMoment)) {
            nextAlertTime = firstAlertTime;
          } else if (now.isBefore(secondMoment)) {
            nextAlertTime = secondAlertTime;
          } else {
            // Both passed - use first time (API will recalculate on next Get())
            nextAlertTime = firstAlertTime;
          }
        } else if (firstAlertTime) {
          nextAlertTime = firstAlertTime;
        } else if (secondAlertTime) {
          nextAlertTime = secondAlertTime;
        }

        if (nextAlertTime) {
          this.addNotification(
            new NotificationData(NotificationType.ScoreReminder, nextAlertTime)
          );
        }
      }

      this.refreshNotifications();
      this.setMorningTimers();
    });
  }

  /**
   * Gets the total count of currently visible notifications.
   *
   * Excludes Plan Reminder: it's not wired into the dialog (its card is commented
   * out there) and defaults to visible until dismissed rather than being scheduled
   * like the two types below, so counting it here would make the badge show 1 with
   * nothing to show for it in the dialog. Revisit once Plan Reminder is fully built out.
   * @returns The number of active Score Reminder / Science Bite / Community notifications
   */
  getNotificationCount() {
    return (
      (this.showScoreAgain ? 1 : 0) +
      (this.showScienceBite ? 1 : 0) +
      this.communityNotifications.length
    );
  }

  /**
   * Adds a Community (NodeBB) notification card, received live via a service worker
   * push message while the app is open (see app.component.ts's COMMUNITY_NOTIFICATION
   * handler). Unlike Score Reminder/Science Bite, these aren't scheduled from settings
   * and have no persisted read/removed state (FLIP-161).
   */
  addCommunityNotification(title: string, body: string, deepLink?: string): void {
    this.communityNotifications.push(
      new CommunityNotificationData(title, body, deepLink)
    );

    const settings = this.settingsService.getSettings();
    if (settings.audioIsOn) {
      const audio = new Audio("../assets/BC_Notification.mp3");
      try {
        audio.play();
      } catch (error) {
        console.log(error);
      }
    }

    this.notificationSource.next(this.notifications);
  }

  /**
   * Dismisses a single Community notification card by its index in
   * communityNotifications.
   */
  removeCommunityNotification(index: number): void {
    this.communityNotifications.splice(index, 1);
    this.notificationSource.next(this.notifications);
  }

  /**
   * Updates an existing notification by removing the old one and adding the new one.
   * @param item - The notification data to update
   */
  updateNotification(item: NotificationData) {
    this.removeNotification(item.notificationType);
    this.addNotification(item);
  }

  /**
   * Plays the notification sound if audio is enabled in settings.
   * Uses the default BrainChanger notification sound file.
   */
  playSoundNotification() {
    const settings = this.settingsService.getSettings();
    const playSound = settings.audioIsOn;

    if (playSound) {
      const audio = new Audio("../assets/BC_Notification.mp3");
      try {
        audio.play();
      } catch (error) {
        console.log(error);
      }
    }
  }

  /**
   * Sends a browser notification using the Web Notifications API.
   * Creates a browser notification that appears in the system notification area.
   * Note: The notification object is created but not stored, as it's automatically
   * managed by the browser's notification system.
   * 
   * @param title - The title of the notification
   * @param opts - Optional notification options (body, image, badge, icon)
   */
  sendNotification(title: string, opts?: NotificationOptions) {
    // Build options object with provided values or null defaults
    // Using 'any' type to allow image property which is supported by the API
    // but not in all TypeScript definitions
    let options: any = {};
    if (opts) {
      options = {
        body: opts.body || null,
        image: (opts as any).image || null,
        badge: opts.badge || null,
        icon: opts.icon || null,
      };
    }

    // Create and display the browser notification
    // The browser will handle displaying and managing this notification
    const notify = new Notification(title, options);
  }

  /**
   * Adds a new notification and schedules it to be displayed at the specified time.
   * Calculates the time difference between now and the notification time, then sets
   * up a timer to trigger the notification. Only schedules if the corresponding
   * alert is enabled in user settings.
   * @param item - The notification data to add and schedule
   */
  addNotification(item: NotificationData) {
    const settings = this.settingsService.getSettings();
    // console.log('[settings]', settings);

    const self = this;
    // Get current time and target notification time
    const currentTime = moment();
    const currentTimeFormatted = currentTime.format("YYYY-MM-DD HH:mm:ss");
    const nextTime = moment(this.getValidDateObject(item.time));
    const nextTimeFormatted = nextTime.format("YYYY-MM-DD HH:mm:ss");
    
    // Calculate milliseconds until notification should trigger
    const timerMilliSeconds = moment
      .duration(
        nextTime.diff(currentTime)
      )
      .asMilliseconds();

    const notificationTypeName = item.notificationType === NotificationType.ScienceBite 
      ? "ScienceBite" 
      : "ScoreReminder";

    // Don't schedule if the time has already passed
    if (timerMilliSeconds <= 0) {
      // console.log(
      //   `[DialogNotificationService] ${notificationTypeName} notification NOT scheduled - time has already passed`,
      //   {
      //     notificationType: notificationTypeName,
      //     scheduledTime: nextTimeFormatted,
      //     currentTime: currentTimeFormatted,
      //     millisecondsUntil: timerMilliSeconds
      //   }
      // );
      return;
    }

    // Add to notifications array if not already present
    const wasAlreadyPresent = this.notifications.findIndex(
      (x) => x.notificationType === item.notificationType
    ) !== -1;
    
    if (!wasAlreadyPresent) {
      this.notifications.push(item);
    }

    // Calculate human-readable time until notification
    const duration = moment.duration(timerMilliSeconds);
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    const timeUntilStr = `${hours}h ${minutes}m ${seconds}s`;

    // Schedule Science Bite notification
    if (item.notificationType === NotificationType.ScienceBite) {
      if (settings.isBrainScienceBiteAlertOn) {
        // Clear any existing timer before setting a new one
        if (
          this.scienceBiteTimer != null ||
          this.scienceBiteTimer !== undefined
        ) {
          clearTimeout(this.scienceBiteTimer);
        }
        // Set timer to trigger notification at scheduled time
        this.scienceBiteTimer = setTimeout(() => {
          self.playNotificationSound(NotificationType.ScienceBite);
          self.setScienceBiteVisibility(true);
          self.notificationSource.next(self.notifications);
        }, timerMilliSeconds);
        
        // console.log(
        //   `[DialogNotificationService] ${notificationTypeName} notification SCHEDULED`,
        //   {
        //     notificationType: notificationTypeName,
        //     scheduledTime: nextTimeFormatted,
        //     currentTime: currentTimeFormatted,
        //     millisecondsUntil: timerMilliSeconds,
        //     timeUntil: timeUntilStr,
        //     alertEnabled: settings.isBrainScienceBiteAlertOn,
        //     wasAlreadyPresent: wasAlreadyPresent
        //   }
        // );
      } else {
        // console.log(
        //   `[DialogNotificationService] ${notificationTypeName} notification NOT scheduled - alert disabled`,
        //   {
        //     notificationType: notificationTypeName,
        //     scheduledTime: nextTimeFormatted,
        //     alertEnabled: settings.isBrainScienceBiteAlertOn
        //   }
        // );
      }
    } else {
      // Schedule Score Reminder notification
      if (settings.isMarginOfSafetyAlertOn) {
        // Clear any existing timer before setting a new one
        if (
          this.scoreAgainTimer != null ||
          this.scoreAgainTimer !== undefined
        ) {
          clearTimeout(this.scoreAgainTimer);
        }
        // Set timer to trigger notification at scheduled time
        this.scoreAgainTimer = setTimeout(() => {
          self.playNotificationSound(NotificationType.ScoreReminder);
          self.setScoreAgainVisibility(true);
          self.notificationSource.next(self.notifications);
        }, timerMilliSeconds);
        
        // console.log(
        //   `[DialogNotificationService] ${notificationTypeName} notification SCHEDULED`,
        //   {
        //     notificationType: notificationTypeName,
        //     scheduledTime: nextTimeFormatted,
        //     currentTime: currentTimeFormatted,
        //     millisecondsUntil: timerMilliSeconds,
        //     timeUntil: timeUntilStr,
        //     alertEnabled: settings.isMarginOfSafetyAlertOn,
        //     wasAlreadyPresent: wasAlreadyPresent
        //   }
        // );
      } else {
        // console.log(
        //   `[DialogNotificationService] ${notificationTypeName} notification NOT scheduled - alert disabled`,
        //   {
        //     notificationType: notificationTypeName,
        //     scheduledTime: nextTimeFormatted,
        //     alertEnabled: settings.isMarginOfSafetyAlertOn
        //   }
        // );
      }
    }
  }

  /**
   * Sets the visibility state of the Score Again (Score Reminder) notification.
   * Updates both the in-memory flag and localStorage for persistence across page reloads.
   * @param visibility - Whether the notification should be visible
   */
  setScoreAgainVisibility(visibility: boolean) {
    this.showScoreAgain = visibility;
    localStorage.setItem(
      "IsScoreAgainNotificationVisible",
      visibility ? "true" : "false"
    );
  }

  /**
   * Sets the visibility state of the Science Bite notification.
   * Updates both the in-memory flag and localStorage for persistence across page reloads.
   * @param visibility - Whether the notification should be visible
   */
  setScienceBiteVisibility(visibility: boolean) {
    this.showScienceBite = visibility;
    localStorage.setItem(
      "IsScienceBiteNotificationVisible",
      visibility ? "true" : "false"
    );
  }

  /**
   * Sets the visibility state of the Plan Reminder notification.
   * Updates both the in-memory flag and localStorage for persistence across page reloads.
   * @param visibility - Whether the notification should be visible
   */
  setPlanReminderVisibility(visibility: boolean) {
    this.showPlanReminder = visibility;
    localStorage.setItem(
      "IsPlanReminderNotificationVisible",
      visibility ? "true" : "false"
    );
  }

  /**
   * Removes a notification by type, hiding it and resetting morning timers.
   *
   * This method:
   * 1. Hides the notification dialog based on the notification type
   * 2. Resets morning timers (though setMorningTimers is currently disabled)
   * 3. Notifies subscribers that the notification state has changed
   *
   * @param type - The type of notification to remove
   */
  removeNotification(type: NotificationType) {
    // Hide the appropriate notification based on type
    if (type === NotificationType.ScienceBite) {
      this.setScienceBiteVisibility(false);
    } else if (type === NotificationType.PlanReminder) {
      this.setPlanReminderVisibility(false);
    } else {
      this.setScoreAgainVisibility(false);
    }
    // Reset morning timers (currently disabled but kept for potential future use)
    this.setMorningTimers();
    // Notify subscribers of the state change
    this.notificationSource.next(this.notifications);
  }

  /**
   * Removes all notifications, hiding Score Again, Science Bite, and Plan Reminder notifications.
   *
   * This method:
   * 1. Hides all notification dialogs
   * 2. Resets morning timers (though setMorningTimers is currently disabled)
   * 3. Notifies subscribers that the notification state has changed
   *
   * Used when the user wants to dismiss all active notifications at once.
   */
  removeAllNotifications() {
    // Hide all notification types
    this.setScoreAgainVisibility(false);
    this.setScienceBiteVisibility(false);
    this.setPlanReminderVisibility(false);
    this.communityNotifications = [];
    // Reset morning timers (currently disabled but kept for potential future use)
    this.setMorningTimers();
    // Notify subscribers of the state change
    this.notificationSource.next(this.notifications);
  }

  /**
   * Gets the current list of all notification data.
   * @returns Array of NotificationData objects
   */
  getNotifications() {
    return this.notifications;
  }

  /**
   * Logs a summary of all scheduled notifications.
   * Should be called after all notifications have been added to see what's scheduled.
   */
  logScheduledNotificationsSummary() {
    const currentTime = moment();
    const currentTimeFormatted = currentTime.format("YYYY-MM-DD HH:mm:ss");
    
    // console.log(
    //   `[DialogNotificationService] === Scheduled Notifications Summary ===`,
    //   {
    //     currentTime: currentTimeFormatted,
    //     totalNotifications: this.notifications.length
    //   }
    // );

    this.notifications.forEach((notification, index) => {
      const notificationTypeName = notification.notificationType === NotificationType.ScienceBite 
        ? "ScienceBite" 
        : "ScoreReminder";
      
      const nextTime = moment(this.getValidDateObject(notification.time));
      const nextTimeFormatted = nextTime.format("YYYY-MM-DD HH:mm:ss");
      const timerMilliSeconds = moment.duration(nextTime.diff(currentTime)).asMilliseconds();
      
      const duration = moment.duration(timerMilliSeconds);
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();
      const timeUntilStr = `${hours}h ${minutes}m ${seconds}s`;
      
      // Check if timer is set
      const timerSet = notification.notificationType === NotificationType.ScienceBite
        ? this.scienceBiteTimer != null
        : this.scoreAgainTimer != null;

      // console.log(
      //   `[DialogNotificationService] Notification ${index + 1}/${this.notifications.length}: ${notificationTypeName}`,
      //   {
      //     notificationType: notificationTypeName,
      //     scheduledTime: nextTimeFormatted,
      //     timeUntil: timeUntilStr,
      //     millisecondsUntil: timerMilliSeconds,
      //     timerSet: timerSet,
      //     willTrigger: timerMilliSeconds > 0 && timerSet
      //   }
      // );
    });

    // console.log(
    //   `[DialogNotificationService] === End Summary ===`
    // );
  }

  /**
   * Plays the notification sound if audio is enabled and the notification isn't already visible.
   * Prevents duplicate sound playback if the notification is already showing.
   * @param type - The type of notification that triggered the sound
   */
  playNotificationSound(type: NotificationType) {
    const settings = this.settingsService.getSettings();
    let playSound = settings.audioIsOn;

    // Don't play if audio is disabled
    if (!playSound) {
      return;
    } else {
      // Check if notification is already visible to avoid duplicate sounds
      if (type === NotificationType.ScienceBite) {
        const sbNotification =
          localStorage.getItem("IsScienceBiteNotificationVisible") === "true";
        // Only play sound if notification is not already visible
        sbNotification ? (playSound = false) : (playSound = true);
      } else {
        const saNotification =
          localStorage.getItem("IsScoreAgainNotificationVisible") === "true";
        // Only play sound if notification is not already visible
        saNotification ? (playSound = false) : (playSound = true);
      }

      // Play the notification sound
      if (playSound) {
        const audio = new Audio("../assets/BC_Notification.mp3");
        try {
          audio.play();
        } catch (error) {
          console.log(error);
        }
      }
    }
  }

  /**
   * Refreshes notification visibility states from localStorage.
   * 
   * Used to restore notification state when the application is reloaded or initialized.
   * This ensures that if notifications were visible before a page refresh, they remain
   * visible after the page reloads. The visibility state is persisted in localStorage
   * by the setScienceBiteVisibility and setScoreAgainVisibility methods.
   * 
   * Notifies subscribers of the current notification state after restoration.
   */
  refreshNotifications() {
    // Restore Science Bite visibility from localStorage
    // localStorage stores "true" or "false" as strings, so we compare to "true"
    localStorage.getItem("IsScienceBiteNotificationVisible") === "true"
      ? (this.showScienceBite = true)
      : (this.showScienceBite = false);
    // Restore Score Again visibility from localStorage
    localStorage.getItem("IsScoreAgainNotificationVisible") === "true"
      ? (this.showScoreAgain = true)
      : (this.showScoreAgain = false);
    // Restore Plan Reminder visibility from localStorage. Unlike the two types above, a
    // missing key means "never dismissed yet" rather than "not yet triggered", so it
    // defaults to visible instead of hidden.
    this.showPlanReminder =
      localStorage.getItem("IsPlanReminderNotificationVisible") !== "false";
    // Notify subscribers of the restored state
    this.notificationSource.next(this.notifications);
  }

  /**
   * Triggers a notification with default "Time to Score" content.
   * 
   * This method sends a browser notification, plays the notification sound,
   * and sets the Science Bite visibility. This method is primarily used by
   * the morning timer system (which is currently disabled).
   * 
   * Note: This method always sets Science Bite visibility regardless of the
   * notification type parameter. This appears to be a legacy behavior that
   * may need to be updated to handle different notification types correctly.
   * 
   * @param type - The type of notification being triggered (though behavior
   *               is currently the same for all types)
   */
  notify(type: NotificationType) {
    // Default notification content
    const title = "Time to Score";
    const options = {
      body: "It's that time of the day to score again and to always have an awesome day.",
      icon: "assets/icons/icon-192x192.png",
      // image: 'assets/images/android-image-notif.png', // Commented out image option
      badge: "assets/images/brain2.png",
    };

    // Send browser notification to the system notification area
    this.sendNotification(title, options);

    // Play notification sound (respects audio settings)
    this.playNotificationSound(type);
    // Note: Always sets Science Bite visibility regardless of type parameter
    // This may be a bug or legacy behavior that should be reviewed
    this.setScienceBiteVisibility(true);
    // Reset morning timers (currently disabled but kept for potential future use)
    this.setMorningTimers();
  }

  /**
   * Sets up timers for morning notifications based on user settings.
   * NOTE: This method is currently disabled (returns early) and the logic below is not executed.
   * It was intended to schedule daily morning notifications for:
   * - Brain Science Bites at the configured start time
   * - Margin of Safety alerts at first and second configured times
   * 
   * The method calculates the time difference between now and the target notification times,
   * accounting for times that have already passed today (schedules for tomorrow instead).
   * Includes a 30-second buffer to avoid duplicate notifications.
   */
  setMorningTimers() {
    // console.log('setMorningTimers notification timers', moment(new Date()));

    // Method is currently disabled - returns immediately
    return;
    
    // Commented out service worker notification code
    // navigator.serviceWorker.getRegistration().then(registration => {
    //   console.log('registered');
    //   setTimeout(() => {
    //     registration.showNotification(title, options);
    //   }, 3000);
    // });
    const self = this;
    const settings = this.settingsService.getSettings();

    // Calculate target times for today's notifications
    const dateStringToday = moment().format("YYYY-MM-DD");
    const currentDateTime = moment(new Date());
    let scienceBitesTime = moment(
      dateStringToday + " " + settings.brainScienceBiteStartTime
    );
    let marginOfSafetyFirstTime = moment(
      dateStringToday + " " + settings.marginOfSafetyStartTime
    );
    let marginOfSafetySecondTime = moment(
      dateStringToday + " " + settings.marginOfSafetySecondTime
    );

    // console.log('times', currentDateTime, scienceBitesTime, marginOfSafetyFirstTime, marginOfSafetySecondTime);

    // If the target time has passed today, schedule for tomorrow
    if (currentDateTime > scienceBitesTime) {
      scienceBitesTime = scienceBitesTime.add(1, "day");
    }
    // Schedule Science Bite notification timeout (+30 seconds buffer to avoid duplicate triggers)
    this.scienceBitesTimeout = setTimeout(() => {
      self.notify(NotificationType.ScienceBite);
    }, scienceBitesTime.diff(currentDateTime) + 30000);

    // If the first Margin of Safety time has passed today, schedule for tomorrow
    if (currentDateTime > marginOfSafetyFirstTime) {
      marginOfSafetyFirstTime = marginOfSafetyFirstTime.add(1, "day");
    }

    // Schedule first Margin of Safety notification timeout (+30 seconds buffer)
    this.marginOfSafetyFirstTimeout = setTimeout(() => {
      self.notify(NotificationType.ScoreReminder);
    }, marginOfSafetyFirstTime.diff(currentDateTime) + 30000);

    // If the second Margin of Safety time has passed today, schedule for tomorrow
    if (currentDateTime > marginOfSafetySecondTime) {
      marginOfSafetySecondTime = marginOfSafetySecondTime.add(1, "day");
    }
    // Schedule second Margin of Safety notification timeout (+30 seconds buffer)
    this.marginOfSafetySecondTimeout = setTimeout(() => {
      self.notify(NotificationType.ScoreReminder);
    }, marginOfSafetySecondTime.diff(currentDateTime) + 30000);

    // Legacy commented-out code for alternative timer calculation approach
    // moment().toDate()

    // const timeNow = moment().format('YYYY-MM-DD HH:mm:ss');
    // const sbMorningTime = moment(timeNow.substring(0, timeNow.indexOf(' ')) + ' ' + settings.brainScienceBiteStartTime)
    //   .add(1, 'days').format('YYYY-MM-DD HH:mm:ss');
    // const saMorningTime = moment(timeNow.substring(0, timeNow.indexOf(' ')) + ' ' + settings.marginOfSafetyStartTime)
    //   .add(1, 'days').format('YYYY-MM-DD HH:mm:ss');
    // const sbTimerMilliSeconds = moment.duration(moment(sbMorningTime, 'YYYY-MM-DD HH:mm:ss')
    //   .diff(moment(timeNow, 'YYYY-MM-DD HH:mm:ss'))).asMilliseconds();
    // const saTimerMilliSeconds = moment.duration(moment(saMorningTime, 'YYYY-MM-DD HH:mm:ss')
    //   .diff(moment(timeNow, 'YYYY-MM-DD HH:mm:ss'))).asMilliseconds();

    // if (settings.isBrainScienceBiteAlertOn && sbTimerMilliSeconds > 0) {

    //   if (this.scienceBiteMorningTimer != null || this.scienceBiteMorningTimer !== undefined) {
    //     clearTimeout(this.scienceBiteMorningTimer);
    //   }

    //   this.scienceBiteMorningTimer = setTimeout(() => {
    //     self.playNotificationSound(NotificationType.ScienceBite);
    //     self.setScienceBiteVisibility(true);
    //     self.notificationSource.next(self.notifications);
    //   }, sbTimerMilliSeconds);

    // }
    // if (settings.isMarginOfSafetyAlertOn && saTimerMilliSeconds > 0) {

    //   if (this.scoreAgainMorningTimer != null || this.scoreAgainMorningTimer !== undefined) {
    //     clearTimeout(this.scoreAgainMorningTimer);
    //   }

    //   this.scoreAgainMorningTimer = setTimeout(() => {
    //     self.playNotificationSound(NotificationType.ScoreReminder);
    //     self.setScoreAgainVisibility(true);
    //     self.notificationSource.next(self.notifications);
    //   }, saTimerMilliSeconds);

    // }
  }

  /**
   * Converts a date string to a valid Date object.
   * Attempts to parse using the native Date constructor first, but falls back to
   * moment.js parsing if the result is invalid or not a Date object.
   * @param dateString - The date string to parse (expected format: "YYYY-MM-DD HH:mm:ss:SSS")
   * @returns A valid Date object
   */
  getValidDateObject(dateString: string): Date {
    let newDateObject = new Date(dateString);

    // Check if the result is a valid Date object
    if (Object.prototype.toString.call(newDateObject) === "[object Date]") {
      // If it's a Date but invalid (NaN), parse with moment.js
      if (isNaN(newDateObject.getTime())) {
        newDateObject = moment(dateString, "YYYY-MM-DD HH:mm:ss:SSS").toDate();
      }
    } else {
      // If not a Date object, parse with moment.js
      newDateObject = moment(dateString, "YYYY-MM-DD HH:mm:ss:SSS").toDate();
    }
    return newDateObject;
  }
}
